import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  ChevronDown,
  Check,
  Upload,
  Brain,
  Zap,
  Search,
  BookMarked,
  ShieldCheck,
  UserCheck,
  Tag,
  Plus,
  X,
  Sparkles,
} from 'lucide-react';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import {
  useAgents,
  useAdminTools,
  useAgentDetail,
  useCreateAgent,
  useModels,
  useSkills,
  useTools,
  useUpdateAgent,
} from '../../hooks';
import { formatSkillName, getSkillId, normalizeSkillsList } from '../SkillsSection/skillsUtils';
import Toast from '../Toast';
import AgentSuccessModal from '../AgentSuccessModal';
import Loaders from '../Loaders';
import { FeatureOverlay } from '../ui';
import { PLATFORM_ID } from '../../constants/platform';

const accentColors = ['#FF6A2B', '#8B5CF6', '#10B981', '#F59E0B', '#EC4899', '#3B82F6', '#EF4444', '#14B8A6'];

const instructionTemplates = [
  {
    id: 'assistant',
    label: 'General Assistant',
    text: 'You are a helpful, concise assistant. Answer questions accurately and ask clarifying questions when needed.',
  },
  {
    id: 'researcher',
    label: 'Research Analyst',
    text: 'You are a rigorous research analyst. Investigate topics thoroughly, cite sources, and present structured findings with key insights and recommendations.',
  },
  {
    id: 'writer',
    label: 'Content Writer',
    text: 'You are an expert content writer. Produce clear, engaging copy tailored to the audience. Adapt tone and style on request.',
  },
  {
    id: 'coder',
    label: 'Code Engineer',
    text: 'You are a senior software engineer. Write clean, well-tested code. Explain decisions, flag edge cases, and suggest improvements.',
  },
  {
    id: 'support',
    label: 'Support Agent',
    text: 'You are a patient customer support agent. Resolve issues empathetically, escalate when appropriate, and always confirm the user\'s problem is solved.',
  },
  {
    id: 'analyst',
    label: 'Data Analyst',
    text: 'You are a data analyst. Interpret data, surface actionable insights, and present results with clear visual descriptions and caveats.',
  },
];

const behaviorTones = ['Concise', 'Detailed', 'Friendly', 'Formal', 'Creative'];

function SimpleSelect({ label, value, options, onChange, accent = '#FF6A2B' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = options.find(o => o.id === value);

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div className="relative">
      <label className="text-sm text-ink mb-1.5 block">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between bg-white border border-shell-border rounded-full px-3 py-2.5 text-sm text-ink hover:border-ink/20">
        <span className={current ? '' : 'text-mute'}>
          {current ? current.label : `Select ${label.toLowerCase()}`}
        </span>
        <ChevronDown className="w-4 h-4 text-mute" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-shell-border rounded-[12px] shadow-soft">
            <div className="relative p-2 border-b border-shell-border">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-mute" />
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="w-full bg-paper border border-shell-border rounded-md pl-8 pr-2 py-1.5 text-xs text-ink placeholder:text-mute outline-none"
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 && <p className="px-3 py-2 text-xs text-mute">No results found.</p>}
              {filtered.map(o => {
                const active = o.id === value;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => {
                      onChange(o.id);
                      setOpen(false);
                      setQuery('');
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-ink hover:bg-paper">
                    <span>{o.label}</span>
                    {active && <Check className="w-3.5 h-3.5" style={{ color: accent }} />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MultiSelect({ label, value, onToggle, options, accent }) {
  const [open, setOpen] = useState(false);
  const selected = value.length > 0;
  return (
    <div className="relative">
      <label className="text-sm text-ink mb-1.5 block">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between bg-white border border-shell-border rounded-full px-3 py-2.5 text-sm text-ink hover:border-ink/20">
        <span className={selected ? '' : 'text-mute'}>
          {selected ? `${value.length} selected` : 'Select to add'}
        </span>
        <ChevronDown className="w-4 h-4 text-mute" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-shell-border rounded-[12px] shadow-soft py-1 max-h-48 overflow-y-auto">
            {options.length === 0 && <p className="px-3 py-2 text-xs text-mute">None available</p>}
            {options.map(opt => {
              const active = value.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onToggle(opt.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-ink hover:bg-paper">
                  <span>{opt.label}</span>
                  {active && <Check className="w-3.5 h-3.5" style={{ color: accent }} />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function SettingToggle({ icon: Icon, title, enabled, onChange, enabledLabel, disabledLabel, description }) {
  return (
    <div className="bg-paper border border-shell-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-4">
        <label className="text-sm text-ink">{title}</label>
        <button
          type="button"
          onClick={() => onChange(!enabled)}
          className={`relative w-10 h-6 rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-[#333]'}`}>
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? 'translate-x-4' : ''
              }`}
          />
        </button>
      </div>
      <div className="flex items-center gap-3 p-3 rounded-[22px] bg-white shadow-soft border border-shell-border">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center ${enabled ? 'bg-primary/10' : 'bg-paper'
            }`}>
          <Icon className={`w-4 h-4 ${enabled ? 'text-primary' : 'text-mute'}`} />
        </div>
        <span className="text-sm text-ink">{enabled ? enabledLabel : disabledLabel}</span>
      </div>
      <p className="text-xs text-mute mt-3 leading-relaxed">{description}</p>
    </div>
  );
}

function parseModelProviders(modelsData) {
  const raw = modelsData?.models;
  if (!Array.isArray(raw)) return [];

  return raw.map(providerObj => {
    const providerName = Object.keys(providerObj)[0];
    const models = providerObj[providerName] || [];
    return {
      id: providerName,
      label: providerName,
      models: models.map(m => ({
        id: m.id,
        label: m.name || m.id,
      })),
    };
  });
}

function findProviderForModel(providers, modelId) {
  const provider = providers.find(p => p.models.some(m => m.id === modelId));
  return provider?.id || '';
}

function resolveIconSrc(icon) {
  if (!icon || typeof icon !== 'string') return null;
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    // Legacy SVG uploads are served by S3 as binary/octet-stream and won't render in <img>.
    // Treat them as "no icon" so the robot + color picker takes over and re-saving heals the agent.
    return icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : icon;
  }
  if (icon.startsWith('data:')) return icon;
  return `data:image/png;base64,${icon}`;
}

export default function AgentBuilder({ agentId, isDuplicate = false }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { user } = useContextHook(AuthContext, v => ({ user: v.user }));
  const [successModal, setSuccessModal] = useState(false);

  const isEdit = !!agentId && !isDuplicate;

  const { data: agentsData } = useAgents();
  const { data: modelsData } = useModels();
  const { data: userToolsData } = useTools('user_created');
  const { data: adminToolsData } = useAdminTools();
  const { data: skillsData } = useSkills();
  const { data: agentDetailData, isFetching: isAgentLoading } = useAgentDetail(agentId);
  const agentDetail = agentDetailData?.detail;

  const modelProviders = useMemo(() => parseModelProviders(modelsData), [modelsData]);
  const toolOptions = useMemo(() => {
    const userTools = userToolsData?.tools || [];
    const adminTools = adminToolsData?.tools || [];
    return [...userTools, ...adminTools].map(t => ({
      id: t.tool_id,
      label: t.tool_name,
    }));
  }, [userToolsData, adminToolsData]);

  const skillOptions = useMemo(
    () =>
      normalizeSkillsList(skillsData).map(s => ({
        id: getSkillId(s),
        label: formatSkillName(s?.name || s?.skill_name),
      })),
    [skillsData],
  );

  const [name, setName] = useState('');
  const [iconColor, setIconColor] = useState(accentColors[0]);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [agentType, setAgentType] = useState('');
  const [template, setTemplate] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [description, setDescription] = useState('');
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [tone, setTone] = useState('Concise');
  const [tools, setTools] = useState([]);
  const [skills, setSkills] = useState([]);
  const [memory, setMemory] = useState(false);
  const [triggers, setTriggers] = useState(true);
  const [knowledge, setKnowledge] = useState([]);
  const [knowledgeInput, setKnowledgeInput] = useState('');
  const [gates, setGates] = useState([]);
  const [datEnabled, setDatEnabled] = useState(false);
  const [datOf, setDatOf] = useState('');
  const [versionLabel, setVersionLabel] = useState('0.1.0');
  const [personaName, setPersonaName] = useState('');
  const [personaTone, setPersonaTone] = useState('Concise');
  const [copilotSurfaces, setCopilotSurfaces] = useState(['chat']);
  const [starterPrompts, setStarterPrompts] = useState([]);
  const [starterInput, setStarterInput] = useState('');

  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (!provider && modelProviders.length > 0 && !agentId) {
      setProvider(modelProviders[0].id);
      if (modelProviders[0].models?.[0]) {
        setModel(modelProviders[0].models[0].id);
      }
    }
  }, [modelProviders, provider, agentId]);

  useEffect(() => {
    if (!agentId || prefilled || !agentDetail || modelProviders.length === 0) return;

    setName(isDuplicate ? `${agentDetail.agent_name || ''} Copy` : agentDetail.agent_name || '');
    setDescription(agentDetail.description || '');
    setInstructions(agentDetail.instructions || '');
    setMemory(agentDetail.enable_user_memories ?? false);
    if (agentDetail.behavior_type) setAgentType(agentDetail.behavior_type);

    if (agentDetail.model_id) {
      setModel(agentDetail.model_id);
      setProvider(findProviderForModel(modelProviders, agentDetail.model_id));
    }
    if (Array.isArray(agentDetail.tools)) {
      setTools(agentDetail.tools);
    }
    if (Array.isArray(agentDetail.skills)) {
      setSkills(agentDetail.skills);
    } else if (typeof agentDetail.skills === 'string' && agentDetail.skills.trim()) {
      setSkills(agentDetail.skills.split(',').map(s => s.trim()).filter(Boolean));
    }
    if (!isDuplicate) {
      const src = resolveIconSrc(agentDetail.icon);
      if (src) setIconPreview(src);
    }

    setPrefilled(true);
  }, [agentId, prefilled, agentDetail, modelProviders, isDuplicate]);

  const createAgent = useCreateAgent({
    onSuccess: () => {
      setSuccessModal(true);
    },
    onError: err => {
      Toast({
        type: 'error',
        message: err?.detail || err?.message || 'Could not create agent',
      });
    },
  });

  const updateAgent = useUpdateAgent({
    onSuccess: response => {
      Toast({ type: 'success', message: response?.detail || 'Agent updated' });
      navigate('/agents');
    },
    onError: err => {
      Toast({
        type: 'error',
        message: err?.detail || err?.message || 'Could not update agent',
      });
    },
  });

  const selectTemplate = t => {
    setTemplate(t.id);
    setInstructions(t.text);
  };

  const toggleIn = (arr, setArr, val) => {
    setArr(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
  };

  const handleIconUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      Toast({ type: 'error', message: 'Icon must be under 5MB' });
      return;
    }

    // SVG uploads break once S3 re-serves them as binary/octet-stream — convert to PNG first.
    if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
      try {
        const svgMarkup = await file.text();
        const pngFile = await svgToPngFile(svgMarkup, file.name.replace(/\.svg$/i, '.png'));
        setIconFile(pngFile);
        setIconPreview(URL.createObjectURL(pngFile));
      } catch (error) {
        console.log(error);
        Toast({ type: 'error', message: 'Could not process SVG icon' });
      }
      return;
    }

    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const labelForId = (options, id) => options.find(o => o.id === id)?.label || id;

  // S3 serves uploads as binary/octet-stream; browsers refuse to render SVGs
  // without the correct MIME type, so icons are always sent as PNG.
  const svgToPngFile = (svgMarkup, fileName, size = 128) =>
    new Promise((resolve, reject) => {
      const blobUrl = URL.createObjectURL(new Blob([svgMarkup], { type: 'image/svg+xml' }));
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        canvas.getContext('2d').drawImage(img, 0, 0, size, size);
        URL.revokeObjectURL(blobUrl);
        canvas.toBlob(blob => {
          if (blob) resolve(new File([blob], fileName, { type: 'image/png' }));
          else reject(new Error('Could not convert icon to PNG'));
        }, 'image/png');
      };
      img.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        reject(new Error('Could not load SVG icon'));
      };
      img.src = blobUrl;
    });

  const createRobotIconFile = color => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <rect width="64" height="64" rx="14" fill="${color}" fill-opacity="0.12"/>
        <path d="M32 13v6M28 13h8" stroke="${color}" stroke-width="3" stroke-linecap="round"/>
        <rect x="16" y="19" width="32" height="27" rx="8" fill="none" stroke="${color}" stroke-width="3"/>
        <circle cx="26" cy="31" r="2.5" fill="${color}"/>
        <circle cx="38" cy="31" r="2.5" fill="${color}"/>
        <path d="M25 39h14M12 29v8M52 29v8M25 51v-5M39 51v-5" stroke="${color}" stroke-width="3" stroke-linecap="round"/>
      </svg>`;
    return svgToPngFile(svg.trim(), 'robot-icon.png');
  };

  /**
   * multipart/form-data payload (create + update):
   *  - Create → POST `${REACT_APP_AGENT_API_URL}/create`
   *  - Edit   → PUT  `${REACT_APP_AGENT_API_URL}/update`
   *
   * Required: icon, agent_name, description, instructions, model_id
   * Optional: user_id, tools, platform_id, agent_type,
   *           enable_user_memories, behavior_type, environment, featured, skills[]
   */
  const handleCreate = async () => {
    if (!agentType) {
      Toast({ type: 'error', message: 'Please select an agent type' });
      return;
    }
    if (!name.trim() || !description.trim() || !instructions.trim() || !model) return;

    if (!isEdit) {
      const agentLimit = user?.plan?.agent_creation_limit;
      const agentCount = agentsData?.agents?.length ?? 0;
      if (agentLimit != null && agentCount >= agentLimit) {
        Toast({ type: 'error', message: 'Agent limit exceeded' });
        return;
      }
    }

    const payload = new FormData();
    if (isEdit) {
      payload.append('agent_id', agentId);
    }

    // Required
    if (iconFile instanceof File) {
      payload.append('icon', iconFile);
    } else if (!isEdit || !iconPreview) {
      // Use the colored robot whenever no uploaded/existing image is available.
      try {
        payload.append('icon', await createRobotIconFile(iconColor));
      } catch (error) {
        console.log(error);
        Toast({ type: 'error', message: 'Could not generate agent icon' });
        return;
      }
    }
    payload.append('agent_name', name.trim());
    payload.append('description', description.trim());
    payload.append('instructions', instructions.trim());
    payload.append('model_id', model);

    // Optional
    if (user?.data?.ID) {
      payload.append('user_id', user.data.ID);
    }
    payload.append('tools', tools.length ? tools.join(',') : '');
    payload.append('platform_id', PLATFORM_ID);
    payload.append('agent_type', 'user_created');
    payload.append('enable_user_memories', memory);
    payload.append('behavior_type', agentType);
    payload.append('environment', '');
    payload.append('featured', false);

    // skills is sent the same way as tools: a comma-joined string
    payload.append('skills', skills.length ? skills.join(',') : '');

    if (isEdit) {
      updateAgent.mutate(payload);
    } else {
      createAgent.mutate(payload);
    }
  };

  const valid = Boolean(agentType) && name.trim() && description.trim() && instructions.trim() && model;
  const activeProviderModels = modelProviders.find(p => p.id === provider)?.models || [];
  const saving = createAgent.isPending || updateAgent.isPending;

  if (agentId && isAgentLoading && !prefilled) {
    return <Loaders pageLoader />;
  }

  return (
    <>
      {successModal && <AgentSuccessModal />}

      <div className="p-8 lg:p-10 max-w-6xl">
        <button
          type="button"
          onClick={() => navigate('/agents')}
          className="flex items-center gap-2 text-sm text-mute hover:text-ink mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Agents
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-ink mb-2 tracking-tight">
            {isEdit ? 'Edit Your Agent' : 'Build Your Agent'}
          </h1>
          <p className="text-mute">
            Name your agent, define its core instructions, configure behavior, and attach tools & skills.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-5">
            <div className="bg-white border border-shell-border rounded-[22px] shadow-soft p-5 space-y-4">
              <h3 className="text-sm font-semibold text-ink">Identity</h3>
              <div>
                <label className="text-sm text-ink mb-1.5 block">
                  <span className="text-red-500">*</span> Agent Name
                </label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Jose"
                  className="w-full bg-white border border-shell-border rounded-full px-3 py-2 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40"
                />
              </div>
              <div>
                <label className="text-sm text-ink mb-1.5 block">Icon</label>
                {iconPreview ? (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={iconPreview} alt="Agent icon" className="w-full h-full object-cover" />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIconFile(null);
                        setIconPreview(null);
                      }}
                      className="flex items-center gap-1.5 text-xs text-mute hover:text-ink">
                      <X className="w-3.5 h-3.5" /> Remove image
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${iconColor}1A` }}>
                      <Bot className="w-5 h-5" style={{ color: iconColor }} />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {accentColors.map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setIconColor(c)}
                          className={`w-6 h-6 rounded-full border-2 ${iconColor === c ? 'border-white' : 'border-transparent'
                            }`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 mt-2 text-mute text-xs hover:text-mute">
                  <Upload className="w-3.5 h-3.5" />
                  {iconPreview ? 'Replace image (JPG, PNG, SVG · max 5mb)' : 'Upload custom icon (JPG, PNG, SVG · max 5mb)'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.svg,image/jpeg,image/png,image/svg+xml"
                  className="hidden"
                  onChange={handleIconUpload}
                />
              </div>
              <div>
                <label className="text-sm text-ink mb-1.5 block">
                  Type <span className="text-primary">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[{ id: 'copilot', label: 'Copilot Agent' }, { id: 'generative', label: 'Generative Agent' }, { id: 'persona', label: 'Persona Agent' }, { id: 'action', label: 'Action Agent' }].map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setAgentType(t.id)}
                      className={`py-2 rounded-lg text-sm capitalize border ${agentType === t.id
                        ? 'border-space bg-space/10 text-ink'
                        : 'border-shell-border text-mute'
                        }`}>
                      {t.label}
                    </button>
                  ))}
                </div>
                {!agentType ? (
                  <p className="mt-1.5 text-xs text-mute">Select a type to continue</p>
                ) : null}
              </div>
            </div>

            <div className="bg-white border border-shell-border rounded-[22px] shadow-soft p-5 space-y-4">
              <h3 className="text-sm font-semibold text-ink">Behavior</h3>
              <div className="grid grid-cols-2 gap-3">
                <SimpleSelect
                  label="Provider"
                  value={provider}
                  options={modelProviders.map(p => ({ id: p.id, label: p.label }))}
                  onChange={pid => {
                    setProvider(pid);
                    const p = modelProviders.find(x => x.id === pid);
                    if (p?.models?.length) setModel(p.models[0].id);
                  }}
                />
                <SimpleSelect
                  label="Model Version"
                  value={model}
                  options={activeProviderModels}
                  onChange={setModel}
                />
              </div>
              <FeatureOverlay title="Coming Soon" subtitle="Tone is under development and will be available soon.">
                <div>
                  <label className="text-sm text-ink mb-1.5 block">Tone</label>
                  <div className="flex flex-wrap gap-1.5">
                    {behaviorTones.map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTone(t)}
                        className={`px-3 py-1.5 rounded-full text-xs border ${tone === t
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-shell-border text-mute'
                          }`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </FeatureOverlay>
            </div>

            <div className="bg-white border border-shell-border rounded-[22px] shadow-soft p-5 space-y-4">
              <h3 className="text-sm font-semibold text-ink">Tools & Skills</h3>
              <MultiSelect
                label="Tools"
                value={tools}
                onToggle={v => toggleIn(tools, setTools, v)}
                options={toolOptions}
                accent="#10B981"
              />
              {tools.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tools.map(t => (
                    <span
                      key={t}
                      className="px-2.5 py-1 rounded-full text-xs bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">
                      {labelForId(toolOptions, t)}
                    </span>
                  ))}
                </div>
              )}
              <MultiSelect
                label="Skills"
                value={skills}
                onToggle={v => toggleIn(skills, setSkills, v)}
                options={skillOptions}
                accent="#F59E0B"
              />
              {skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {skills.map(s => (
                    <span
                      key={s}
                      className="px-2.5 py-1 rounded-full text-xs bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20">
                      {labelForId(skillOptions, s)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <FeatureOverlay
              title="Coming Soon"
              subtitle="Composition & Governance is under development and will be available soon.">
              <div className="bg-white border border-shell-border rounded-[22px] shadow-soft p-5 space-y-4">
                <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
                  <BookMarked className="w-4 h-4 text-primary" /> Composition & Governance
                </h3>

                <div>
                  <label className="text-sm text-ink mb-1.5 block">Knowledge Bindings</label>
                  <div className="flex gap-2">
                    <input
                      value={knowledgeInput}
                      onChange={e => setKnowledgeInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && knowledgeInput.trim()) {
                          e.preventDefault();
                          setKnowledge([...knowledge, knowledgeInput.trim()]);
                          setKnowledgeInput('');
                        }
                      }}
                      placeholder="knowhow://domain/source"
                      className="flex-1 bg-white border border-shell-border rounded-full px-3 py-2 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (knowledgeInput.trim()) {
                          setKnowledge([...knowledge, knowledgeInput.trim()]);
                          setKnowledgeInput('');
                        }
                      }}
                      className="px-3 rounded-lg bg-paper hover:bg-paper-2 text-ink">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  {knowledge.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {knowledge.map(k => (
                        <span
                          key={k}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">
                          {k}
                          <button type="button" onClick={() => setKnowledge(knowledge.filter(x => x !== k))}>
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm text-ink mb-1.5 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#F59E0B]" /> AIAHH Gates
                  </label>
                  <p className="text-xs text-mute mb-2">
                    Select tools that require human approval before execution.
                  </p>
                  {tools.length === 0 ? (
                    <p className="text-xs text-mute">Attach a tool first to configure gates.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {tools.map(t => {
                        const active = gates.includes(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setGates(active ? gates.filter(x => x !== t) : [...gates, t])}
                            className={`px-2.5 py-1 rounded-full text-xs border ${active
                              ? 'border-[#F59E0B] bg-[#F59E0B]/10 text-[#F59E0B]'
                              : 'border-shell-border text-mute'
                              }`}>
                            {labelForId(toolOptions, t)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-paper border border-shell-border">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-[#8B5CF6]" />
                    <div>
                      <p className="text-sm text-ink">Digital Agentic Twin (DAT)</p>
                      <p className="text-xs text-mute">Model this agent on a named specialist.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDatEnabled(!datEnabled)}
                    className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${datEnabled ? 'bg-primary' : 'bg-[#333]'
                      }`}>
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${datEnabled ? 'translate-x-4' : ''
                        }`}
                    />
                  </button>
                </div>
                {datEnabled && (
                  <input
                    value={datOf}
                    onChange={e => setDatOf(e.target.value)}
                    placeholder="Specialist name (e.g. Jose Mourinho)"
                    className="w-full bg-white border border-shell-border rounded-full px-3 py-2 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40"
                  />
                )}

                <div>
                  <label className="text-sm text-ink mb-1.5 flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5 text-[#3B82F6]" /> Version Label
                  </label>
                  <input
                    value={versionLabel}
                    onChange={e => setVersionLabel(e.target.value)}
                    placeholder="0.1.0"
                    className="w-full bg-white border border-shell-border rounded-full px-3 py-2 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40"
                  />
                  <p className="text-xs text-mute mt-1.5">
                    Prior versions remain immutable; edits create a new version.
                  </p>
                </div>
              </div>
            </FeatureOverlay>


            {agentType === 'copilot' && (
              <FeatureOverlay title="Coming Soon" subtitle="Copilot Surfaces & Persona is under development and will be available soon.">
                <div className="bg-white border border-shell-border rounded-[22px] shadow-soft p-5 space-y-4">
                  <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" /> Copilot Surfaces & Persona
                  </h3>
                  <div>
                    <label className="text-sm text-ink mb-1.5 block">Surfaces</label>
                    <div className="flex flex-wrap gap-2">
                      {['chat', 'canvas', 'embed'].map(s => {
                        const active = copilotSurfaces.includes(s);
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() =>
                              setCopilotSurfaces(
                                active ? copilotSurfaces.filter(x => x !== s) : [...copilotSurfaces, s],
                              )
                            }
                            className={`px-3 py-1.5 rounded-full text-xs capitalize border ${active
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-shell-border text-mute'
                              }`}>
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      value={personaName}
                      onChange={e => setPersonaName(e.target.value)}
                      placeholder="Persona name"
                      className="bg-white border border-shell-border rounded-full px-3 py-2 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40"
                    />
                    <input
                      value={personaTone}
                      onChange={e => setPersonaTone(e.target.value)}
                      placeholder="Tone"
                      className="bg-white border border-shell-border rounded-full px-3 py-2 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-ink mb-1.5 block">Starter Prompts</label>
                    <div className="flex gap-2">
                      <input
                        value={starterInput}
                        onChange={e => setStarterInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && starterInput.trim()) {
                            e.preventDefault();
                            setStarterPrompts([...starterPrompts, starterInput.trim()]);
                            setStarterInput('');
                          }
                        }}
                        placeholder="Add a starter prompt"
                        className="flex-1 bg-white border border-shell-border rounded-full px-3 py-2 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (starterInput.trim()) {
                            setStarterPrompts([...starterPrompts, starterInput.trim()]);
                            setStarterInput('');
                          }
                        }}
                        className="px-3 rounded-lg bg-paper hover:bg-paper-2 text-ink">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    {starterPrompts.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {starterPrompts.map(p => (
                          <span
                            key={p}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-[#8B5CF6]/10 text-[#8B5CF6] border border-[#8B5CF6]/20">
                            {p}
                            <button
                              type="button"
                              onClick={() => setStarterPrompts(starterPrompts.filter(x => x !== p))}>
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </FeatureOverlay>
            )}
          </div>

          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white border border-shell-border rounded-[22px] shadow-soft p-5">
              <FeatureOverlay
                title="Coming Soon"
                subtitle="Core Instructions is under development and will be available soon.">
                <h3 className="text-sm font-semibold text-ink mb-3">Core Instructions</h3>
                <p className="text-xs text-mute mb-4">
                  Start from a template, then refine the instructions to fit your use case.
                </p>
                <div className="grid sm:grid-cols-2 gap-2 mb-4">
                  {instructionTemplates.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => selectTemplate(t)}
                      className={`text-left p-3 rounded-lg border ${template === t.id ? 'border-primary bg-primary/5' : 'border-shell-border hover:border-ink/20'
                        }`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-ink">{t.label}</p>
                        {template === t.id && <Check className="w-3.5 h-3.5 text-primary" />}
                      </div>
                      <p className="text-xs text-mute line-clamp-2">{t.text}</p>
                    </button>
                  ))}
                </div>
              </FeatureOverlay>
              <label className="text-sm text-ink mb-1.5 block">
                <span className="text-red-500">*</span> Instructions
              </label>
              <textarea
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                rows={10}
                placeholder="Define what your agent does, its role, and how it should behave..."
                className="w-full bg-paper border border-shell-border rounded-lg px-3 py-3 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40 resize-none leading-relaxed"
              />
            </div>

            <div>
              <label className="text-sm text-ink mb-1.5 block">
                <span className="text-[#ef4444]">*</span> Description
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="Describe what this agent does"
                className="w-full bg-white border border-shell-border rounded-lg px-3 py-2.5 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40 resize-none"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <SettingToggle
                icon={Brain}
                title="Memory"
                enabled={memory}
                onChange={setMemory}
                enabledLabel="Memory Enabled"
                disabledLabel="Memory Disabled"
                description="Remember user preferences and context across conversations."
              />
              <SettingToggle
                icon={Zap}
                title="Triggers"
                enabled={triggers}
                onChange={setTriggers}
                enabledLabel="Triggers Enabled"
                disabledLabel="Triggers Disabled"
                description="Activate on events and schedules."
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => navigate('/agents')}
                className="px-5 py-2.5 text-sm text-mute hover:text-ink">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={!valid || saving}
                className="px-6 py-2.5 rounded-full text-sm font-medium bg-spacemint hover:bg-spacemint/85 text-spaceblack disabled:opacity-50">
                {saving
                  ? isEdit
                    ? 'Updating...'
                    : 'Creating...'
                  : isEdit
                    ? 'Update Agent'
                    : 'Create Agent'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
